import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import SafeJsonLd from '@/components/SafeJsonLd';

export interface FAQItem {
  question: string;
  answer: string;
}

interface ServiceFAQProps {
  eyebrow: string;
  heading: string;
  subtitle?: string;
  faqs: FAQItem[];
}

const ServiceFAQ: React.FC<ServiceFAQProps> = ({ eyebrow, heading, subtitle, faqs }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  return (
    <>
      <SafeJsonLd data={schema} />
      <section className="relative py-20 md:py-28 bg-secondary/40 border-y border-border/60" aria-labelledby="service-faq-heading">
        <div className="pointer-events-none absolute inset-0 dot-grid opacity-[0.3]" aria-hidden="true" />

        <div className="relative mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-12 gap-6 mb-14">
            <div className="col-span-12 md:col-span-7">
              <span className="eyebrow">— {eyebrow}</span>
              <h2
                id="service-faq-heading"
                className="mt-4 font-display text-foreground text-[clamp(2rem,4vw,3.25rem)] leading-[1.05] tracking-tight"
              >
                {heading}
              </h2>
            </div>
            {subtitle && (
              <p className="col-span-12 md:col-span-4 md:col-start-9 self-end text-muted-foreground text-base md:text-lg leading-relaxed">
                {subtitle}
              </p>
            )}
          </div>

          <Accordion
            type="single"
            collapsible
            className="w-full border border-border/60 rounded-2xl overflow-hidden bg-card/60 backdrop-blur-sm"
          >
            {faqs.map((faq, idx) => (
              <AccordionItem
                key={idx}
                value={`item-${idx}`}
                className="border-b border-border/60 last:border-0 px-4 md:px-8 data-[state=open]:bg-card transition-colors"
              >
                <AccordionTrigger className="text-left text-base md:text-lg font-medium text-foreground hover:no-underline hover:text-lime smooth-transition py-6 [&[data-state=open]>svg]:rotate-180 [&[data-state=open]]:text-lime">
                  <span className="flex items-center gap-5">
                    <span className="font-mono-tag text-xs uppercase tracking-[0.32em] text-muted-foreground/70 hidden sm:inline">
                      / 0{idx + 1}
                    </span>
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-6 leading-relaxed text-sm md:text-base md:pl-[68px]">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </>
  );
};

export default ServiceFAQ;
